import { buildCloudFrontUrl, getExtension } from '@/utils/pathUtils';
import { uploadImageToS3 } from '@/utils/imageUtils';
import { toggleElementColorOption, removeEmptyGroups } from '@/utils/elementsUtils';
import { isObjectOrStringifiedObject } from '@/utils/objectUtils';
import { v4 as uuidv4 } from 'uuid';
import * as mapUtils from '../mapUtils';
import * as starMapUtils from '../starMapUtils';
import * as sourceElementUtils from '../sourceElementUtils';
import { getPageEpsIdObject, updatePageEpsVisibilityAndLocked } from '../pageEpsUtil.js';
import { generateCustomilyCalendar } from '@/utils/customilyCalendarUtils';
import { tryParseJson } from '@/utils/objectUtils';
import fontManagerService from '@/utils/fontManagerService';
import {
    loadClipPathFromObject,
    sanitizeSvgString,
    parseSvgMaskShape,
    applySvgMaskClipPath
} from '@/utils/fabricUtils';
import { parsePatternConfig } from '@/utils/patternEffects';
import { resetImagePatternLoad, trackImagePatternOnLoad } from '@/utils/imagePatternReplicas';
import { isFullMaskBySrc, isImagePatternMember } from '@/utils/imagePatternMasks';
import { sharedPatternImage } from '@/utils/imagePatternImages';
import { applyTextShapeOption, normalizeTextShapeConfig, serializeTextShapeConfig } from '@/utils/textShapeUtils';
import {
    preparePreviewStep2GroupData,
    shouldCleanupEmptyGroupsOnStep2Load
} from '../groups/previewStep2GroupLoadUtils';

(function () {
    'ngInject';

    angular.module('app.shared.services').factory('PreviewUtilService', previewUtilService);

    function previewUtilService(
        ProductService,
        ProductAPIService,
        MultipleFontService,
        GeneralMathUtils,
        ElementsColorService,
        TestImageService,
        DynamicImageService,
        ImageUtilService,
        DynamicVectorService,
        CanvasUtilService,
        TextureService,
        ExecutionService,
        TextService,
        LibrariesAPIService
    ) {
        /**
         * Load elements into step 2
         * @param {CanvasEx} canvas
         * @param {unknown[]} fonts
         * @param {boolean} previewMode
         * @param {string} username
         * @param {unknown[]} texts
         * @param {unknown[]} circularTexts
         * @param {unknown[]} imagePlaceHolders
         * @param {unknown[]} vectorsEps
         * @param {boolean} loadFromStep1
         * @param {boolean} needColorsUpgrade
         * @param {boolean} forProductTemplate
         * @param {PodProductImagesMetadata} colorOptionsMetadata
         * @param {ElementsGroup[]} elementsGroups
         * @param {boolean} forMyProductBases
         * @param {boolean} isChildTemplateProduct
         * @param {ElementsPageEps[]} listPageEps
         * @param {ElementsGroup[]} groupsEps
         * @param {boolean} comesFromStep1
         * @param {boolean} editMode
         * @param {Set<string>} previewElementIds
         */
        async function loadStep2IntoCanvas(
            canvas,
            fonts,
            previewMode,
            username,
            texts,
            circularTexts,
            imagePlaceHolders,
            vectorsEps,
            loadFromStep1,
            needColorsUpgrade,
            forProductTemplate,
            colorOptionsMetadata,
            elementsGroups,
            forMyProductBases,
            isChildTemplateProduct,
            listPageEps,
            groupsEps,
            comesFromStep1,
            editMode,
            previewElementIds
        ) {
            resetImagePatternLoad(canvas);
            const tasks = [];
            const listEPS = ProductService.getListEPS();

            if (loadFromStep1) {
                canvas.elementsGroups = [];
            }

            const { groupsToLoad, resolveLoadedElementGroupId, resolvedCollections } = preparePreviewStep2GroupData({
                elementsGroups,
                groupsEps,
                listEPS,
                loadFromStep1,
                collections: {
                    texts,
                    circularTexts,
                    imagePlaceHolders,
                    vectorsEps
                }
            });
            texts = resolvedCollections.texts;
            circularTexts = resolvedCollections.circularTexts;
            imagePlaceHolders = resolvedCollections.imagePlaceHolders;
            vectorsEps = resolvedCollections.vectorsEps;

            for (const page of listPageEps) {
                const epsElement = {
                    ...page,
                    visible: true,
                    locked: false,
                    name: page.name,
                    zIndex: page.zIndexPreview,
                    type: 'preview',
                    uuid: page.uuid || uuidv4()
                };
                canvas.addPageEps(epsElement, true);
            }

            try {
                const payload = LibrariesAPIService.getLibraryPositionsFromPreview({
                    texts,
                    circularTexts,
                    imagePlaceHolders,
                    vectorsEps
                });
                await LibrariesAPIService.getLibrariesElementAtPosition(payload);
            } catch (error) {
                console.error('Error loading library element:', error);
            }

            console.log('Adding texts');
            if (texts) {
                for (const element of texts) {
                    const loadText = async function (textProperties) {
                        const aFontFamily = fontManagerService.findFontByPath(textProperties.fontPath);

                        const pageEpsId = getPageEpsIdObject(textProperties, listEPS);

                        // Load the text position and size
                        const objectCenterX = textProperties.centerX;
                        const objectCenterY = textProperties.centerY;
                        const boundingRectWidth = textProperties.width;
                        const boundingRectHeight = textProperties.height;
                        const originalWidth = textProperties.width;

                        let fontsMap = new Map();
                        let fontColorsMap = new Map();
                        MultipleFontService.initializeFontsMap(fontsMap, aFontFamily.Path, textProperties);
                        const initializedFontColorsMap = MultipleFontService.initializeFontColorsMap(
                            fontColorsMap,
                            textProperties
                        );
                        if (initializedFontColorsMap && typeof initializedFontColorsMap === 'object') {
                            fontColorsMap = initializedFontColorsMap;
                        }
                        let color = MultipleFontService.parseColorFromProperty(textProperties.color);
                        let outlineColor = MultipleFontService.parseColorFromProperty(textProperties.outlineColor);
                        const outlineColorsMap = MultipleFontService.parseColorsMap(textProperties.outlineColorsMap);
                        const clipPathObject =
                            textProperties.clipPath && typeof textProperties.clipPath === 'string'
                                ? tryParseJson(textProperties.clipPath)
                                : textProperties.clipPath;

                        const strokeColorsMap = new Map();
                        MultipleFontService.initializeStrokeColor(strokeColorsMap, textProperties);
                        const strokeColor = MultipleFontService.parseColorFromProperty(textProperties.strokeColor);

                        const shadowColorsMap = MultipleFontService.getShadowColorsMap(textProperties);
                        const shadowColor = MultipleFontService.parseColorFromProperty(
                            textProperties.shadowColor ?? shadowColorsMap.get(1)
                        );

                        const transformOptions =
                            textProperties.transformOptions && typeof textProperties.transformOptions === 'string'
                                ? JSON.parse(textProperties.transformOptions)
                                : textProperties.transformOptions || {};

                        const parsedMetadata = tryParseJson(textProperties.metadata);
                        const innerTextMetadata =
                            parsedMetadata && typeof parsedMetadata === 'object' ? parsedMetadata : {};

                        const obj = {
                            version: textProperties.Version,
                            width: boundingRectWidth,
                            height: boundingRectHeight,
                            originX: 'center',
                            originY: 'center',
                            left: objectCenterX,
                            top: objectCenterY,
                            strokeDashArray: canvas.getDashedArray(),
                            angle: textProperties.rotation,
                            centeredRotation: true,
                            textAlign: textProperties.textAlign,
                            textVerticalAlign: textProperties.textVerticalAlign,
                            skewX: textProperties.skewX,
                            skewY: textProperties.skewY,
                            disableSkewing: false,
                            lockUniScalingWithSkew: true, // Proportional scaling with skew
                            lockScalingFlip: true,

                            originalMinSizePx: textProperties.minSizePx,
                            originalMaxSizePx: textProperties.maxSizePx,
                            originalWidth: originalWidth,
                            originalHeight: textProperties.height,
                            originalTop: objectCenterY,
                            originalLeft: objectCenterX,

                            multiline: textProperties.multiline,
                            verticalText: textProperties.verticalText,
                            caps: textProperties.caps,
                            fontPath: aFontFamily.Path,
                            fontName: aFontFamily.Name,

                            color: color,
                            outlineColor: outlineColor,
                            outlineColorLibraryId: textProperties.outlineColorLibraryId,
                            outlineColorsMap: outlineColorsMap,
                            fontLibraryId: textProperties.fontLibraryId,
                            colorLibraryId: textProperties.colorLibraryId,

                            fontOption: 1,
                            fontColorOption: 1,
                            outlineColorOption: 1,
                            id: textProperties.id,
                            uuid: textProperties.uuid,
                            zIndex: textProperties.zIndex,
                            fontsMap: fontsMap,
                            fontColorsMap: fontColorsMap,
                            Itext: textProperties.Itext,
                            textures: textProperties.textures,

                            tracking: textProperties.tracking,
                            addedOnEdit: textProperties.addedOnEdit,
                            lineSpacing: textProperties.lineSpacing,

                            locked: !textProperties.autofillSource && textProperties.locked,
                            visible: !textProperties.autofillSource && textProperties.visible,
                            name: textProperties.name,
                            prefix: textProperties.prefix,
                            suffix: textProperties.suffix,
                            betweenCharacters: innerTextMetadata.betweenCharacters || '',
                            betweenWords: innerTextMetadata.betweenWords || '',
                            ligatures: textProperties.ligatures,
                            isOverlay: textProperties.isOverlay,
                            isPrintArea: textProperties.isPrintArea,
                            groupId: resolveLoadedElementGroupId(textProperties),
                            pageEpsId: pageEpsId || textProperties.pageEpsId,
                            allowEdit: textProperties.allowEdit,
                            oldAllowEdit: textProperties.oldAllowEdit,
                            /*If the text uses the previous version of allowEdit (without a bounding box), these properties are left as null.
                            Once they have a value, the new version is considered active.*/
                            originalInitCenterX: textProperties.oldAllowEdit ? null : textProperties.initCenterX,
                            originalInitCenterY: textProperties.oldAllowEdit ? null : textProperties.initCenterY,
                            initRotation: textProperties.oldAllowEdit ? null : textProperties.initRotation,
                            originalInitFontSize: textProperties.oldAllowEdit ? null : textProperties.initFontSize,
                            /* --- */
                            clipPath: clipPathObject ? await loadClipPathFromObject(clipPathObject) : null,
                            text: textProperties.testingText || textProperties.text || '',
                            isTextPath: textProperties.isTextPath || false,
                            textPath: textProperties.textPath || null,
                            textPathAutoFit: textProperties.textPathAutoFit !== false,
                            transformOptions: transformOptions,
                            fontFamily: aFontFamily.Name + '_' + aFontFamily.Style,
                            needUpdatePreview: false,
                            patternConfig: parsePatternConfig(textProperties.patternConfig),
                            shapeConfig: normalizeTextShapeConfig(textProperties.shapeConfig),
                            useAsMask: textProperties.useAsMask || false,
                            useAsCutout: textProperties.useAsCutout || false,
                            autofillSource: textProperties.autofillSource || '',
                            dateFormat: textProperties.dateFormat || {},
                            preventEditMode: true,
                            editMode,
                            canvasWidth: canvas.width,
                            canvasHeight: canvas.height,
                            noScaleTextWithBox: true,
                            /* stroke */
                            strokeColor: strokeColor,
                            strokeWidth: textProperties.strokeWidth || 0,
                            strokeColorsMap: strokeColorsMap,
                            strokeColorLibraryId: textProperties.strokeColorLibraryId,
                            /* shadow */
                            shadowType: textProperties.shadowType,
                            shadowOffset: textProperties.shadowOffset,
                            shadowAngle: textProperties.shadowAngle,
                            shadowBlur: textProperties.shadowBlur,
                            shadowColor: shadowColor,
                            shadowColorsMap: shadowColorsMap,
                            shadowColorLibraryId: textProperties.shadowColorLibraryId,
                            metadata: textProperties.metadata,
                            isPersonalizableText: textProperties.isPersonalizableText,
                            defaultValue: textProperties.defaultValue
                        };

                        if (textProperties.isTextPath) {
                            obj.needUpdatePreview = textProperties.needUpdatePreview || textProperties.addedOnEdit;
                            obj.fontSize =
                                textProperties.fontSize ?? textProperties.minSizePx ?? textProperties.maxSizePx;
                        }

                        let text = new (obj.isTextPath ? fabric.TextTransform : fabric.BoundedText)(obj);
                        if (!obj.isTextPath && obj.shapeConfig) {
                            await applyTextShapeOption(text).catch(console.error);
                        }
                        if (textProperties.testingText?.length) {
                            text.setText(textProperties.testingText);
                            text.text = textProperties.testingText;
                        }

                        if (textProperties.text?.length) {
                            text.setText(textProperties.text);
                            text.text = textProperties.text;
                        }
                        if (textProperties.isPersonalizableText === false && textProperties.defaultValue) {
                            text.setText(textProperties.defaultValue);
                            text.text = textProperties.defaultValue;
                        }

                        text.setMinSizePx(GeneralMathUtils.calculateFontSizesFromObject(text).min);
                        text.setMaxSizePx(GeneralMathUtils.calculateFontSizesFromObject(text).max);

                        if (text.allowEdit && text.originalInitCenterX != null) {
                            const values = GeneralMathUtils.calculateInitTextValuesFromObject(text);
                            text.setInitPositionAndSize(values.initCenterX, values.initCenterY, values.initFontSize);
                        }

                        text.setColor(ElementsColorService.colorToWeb(color));
                        text.setFontFamily(aFontFamily.Name + '_' + aFontFamily.Style);
                        text.setCaps(textProperties.caps);
                        text.setOutlineColor(ElementsColorService.colorToWeb(outlineColor));
                        text.setOutlineWidth(textProperties.outlineWidth);

                        if (obj.isTextPath) {
                            text.setStrokeColor(ElementsColorService.colorToWeb(strokeColor));
                            text.setStrokeWidth(textProperties.strokeWidth || 0);
                        } else {
                            text.setStrokeWidth(canvas.getVisibleStrokeWidth());
                            text.setShadowColor(ElementsColorService.colorToWeb(shadowColor));
                        }

                        if (previewMode) {
                            text.lockMovementY = true;
                            text.lockMovementX = true;
                            text.lockScalingX = true;
                            text.lockScalingY = true;
                            text.lockRotation = true;
                            if (!obj.isTextPath) {
                                text.setStrokeWidth(0);
                            }
                        }
                        text.setFlip(
                            !loadFromStep1 ? textProperties.flipX : false,
                            !loadFromStep1 ? textProperties.flipY : false
                        );

                        // Set the first position for color, font and textures
                        await Promise.all([
                            MultipleFontService.changeFontColorOption(null, text, 1, canvas),
                            MultipleFontService.changeFontOption(text, 1, canvas),
                            MultipleFontService.changeOutlineColorOption(text, 1, canvas)
                        ]);
                        if (text.textures && text.textures.length) {
                            await TextureService.changeFontTexture(text.textures[0].texturePath, text, 1);
                            await TextureService.changeScaleTexture(text, 1, text.textures[0].textureScale);
                            if (text.shadowType != null) {
                                text.set?.('shadowType', null);
                                text.shadowType = null;
                            }
                        }
                        if (textProperties.isTextPath) {
                            await MultipleFontService.changeStrokeColorOption(text, 1, canvas);
                        } else {
                            await MultipleFontService.changeShadowColorOption(text, 1, canvas);
                        }
                        return text;
                    };

                    tasks.push(async () => await loadText(element));
                }
            }

            console.log('Adding circular texts');
            if (circularTexts) {
                for (const element of circularTexts) {
                    const loadCircularText = async function (textProperties) {
                        const aFontFamily = fontManagerService.findFontByPath(textProperties.fontPath);
                        const parsedMetadata = tryParseJson(textProperties.metadata);
                        const innerTextMetadata =
                            parsedMetadata && typeof parsedMetadata === 'object' ? parsedMetadata : {};

                        const pageEpsId = getPageEpsIdObject(textProperties, listEPS);

                        // Load the text position and size
                        const objectCenterX = textProperties.centerX;
                        const objectCenterY = textProperties.centerY;
                        const horizontalDiameter = textProperties.horizontalDiameter;
                        const verticalDiameter = textProperties.verticalDiameter;
                        const clipPathObject =
                            textProperties.clipPath && typeof textProperties.clipPath === 'string'
                                ? tryParseJson(textProperties.clipPath)
                                : textProperties.clipPath;

                        let fontsMap = new Map();
                        let fontColorsMap = new Map();
                        MultipleFontService.initializeFontsMap(fontsMap, aFontFamily.Path, textProperties);
                        MultipleFontService.initializeFontColorsMap(fontColorsMap, textProperties);
                        let color = MultipleFontService.parseColorFromProperty(textProperties.color);
                        const outlineColor = MultipleFontService.parseColorFromProperty(textProperties.outlineColor);
                        const outlineColorsMap = MultipleFontService.parseColorsMap(textProperties.outlineColorsMap);

                        textProperties.maxSizePx ??= textProperties.fontSize;
                        textProperties.minSizePx ??= textProperties.fontSize;

                        const text = new fabric.PathIText('A', {
                            pathWidth: canvas.getVisibleStrokeWidth(),
                            originX: 'center',
                            originY: 'center',
                            left: objectCenterX,
                            top: objectCenterY,
                            angle: textProperties.rotation,
                            centeredRotation: true,
                            textAlign: textProperties.textAlign,
                            skewX: textProperties.skewX,
                            skewY: textProperties.skewY,
                            disableSkewing: false,
                            lockUniScalingWithSkew: true, // Proportional scaling with skew
                            lockScalingFlip: true,

                            startAngle: textProperties.startAngle,
                            endAngle: textProperties.endAngle,
                            lockAngleControls: true,

                            originalFontSize: textProperties.fontSize,
                            originalMinSizePx: textProperties.minSizePx,
                            originalMaxSizePx: textProperties.maxSizePx,
                            originalHorizontalDiameter: textProperties.horizontalDiameter,

                            fontSize: GeneralMathUtils.calculateCircularFontSizeFromDimensions(
                                horizontalDiameter,
                                textProperties.horizontalDiameter,
                                textProperties.fontSize
                            ),
                            minSizePx: GeneralMathUtils.calculateCircularFontSizeFromDimensions(
                                horizontalDiameter,
                                textProperties.horizontalDiameter,
                                textProperties.minSizePx
                            ),
                            maxSizePx: GeneralMathUtils.calculateCircularFontSizeFromDimensions(
                                horizontalDiameter,
                                textProperties.horizontalDiameter,
                                textProperties.maxSizePx
                            ),
                            horizontalDiameter: horizontalDiameter,
                            verticalDiameter: verticalDiameter,
                            convex: textProperties.convex,
                            tracking: textProperties.tracking,

                            caps: textProperties.caps,
                            fontPath: aFontFamily.Path,
                            fontName: aFontFamily.Name + '_' + aFontFamily.Style,
                            fontFamily: aFontFamily.Name + '_' + aFontFamily.Style,

                            fill: ElementsColorService.colorToWeb(color), // RGB color used as fill
                            color: color, // Color object
                            fontLibraryId: textProperties.fontLibraryId,
                            colorLibraryId: textProperties.colorLibraryId,
                            outlineColorLibraryId: textProperties.outlineColorLibraryId,
                            outlineColorOption: 1,
                            outlineColorsMap: outlineColorsMap,

                            stroke: textProperties.outlineColor,
                            strokeWidth: textProperties.outlineWidth,
                            id: textProperties.id,
                            uuid: textProperties.uuid || uuidv4(),
                            zIndex: typeof textProperties.zIndex === 'undefined' ? 0 : textProperties.zIndex,
                            // add fonts map
                            fontsMap: fontsMap,
                            // add font colors map
                            fontColorsMap: fontColorsMap,
                            addedOnEdit: textProperties.addedOnEdit,

                            locked: textProperties.locked,
                            visible: textProperties.visible,
                            name: textProperties.name,
                            defaultValue: textProperties.defaultValue || '',
                            isPersonalizableText: textProperties.isPersonalizableText !== false,
                            prefix: textProperties.prefix,
                            suffix: textProperties.suffix,
                            betweenCharacters:
                                textProperties.betweenCharacters || innerTextMetadata.betweenCharacters || '',
                            betweenWords: textProperties.betweenWords || innerTextMetadata.betweenWords || '',
                            isOverlay: textProperties.isOverlay,
                            isPrintArea: textProperties.isPrintArea,
                            groupId: resolveLoadedElementGroupId(textProperties),
                            pageEpsId: pageEpsId || textProperties.pageEpsId,
                            text: textProperties.testingText ?? 'A',
                            clipPath: clipPathObject ? await loadClipPathFromObject(clipPathObject) : null
                        });

                        text.setOutlineColor(ElementsColorService.colorToWeb(outlineColor || textProperties.stroke));
                        if (previewMode) {
                            text.lockMovementY = true;
                            text.lockMovementX = true;
                            text.lockScalingX = true;
                            text.lockScalingY = true;
                            text.lockRotation = true;
                            text.set('pathWidth', 0);
                        }
                        text.setFlip(
                            !loadFromStep1 ? textProperties.flipX : false,
                            !loadFromStep1 ? textProperties.flipY : false
                        );
                        if (textProperties.text?.length) {
                            text.text = textProperties.text;
                        }
                        if (textProperties.isPersonalizableText === false && textProperties.defaultValue) {
                            text.text = textProperties.defaultValue;
                        }

                        // Set the first position for color and font
                        await Promise.all([
                            MultipleFontService.changeFontColorOption(null, text, 1, canvas),
                            MultipleFontService.changeFontOption(text, 1, canvas),
                            MultipleFontService.changeOutlineColorOption(text, 1, canvas)
                        ]);

                        return text;
                    };

                    tasks.push(async () => await loadCircularText(element));
                }
            }

            console.log('Adding image placeholders');
            // Load placeholder images
            if (imagePlaceHolders) {
                for (const element of imagePlaceHolders) {
                    const pageEpsId = getPageEpsIdObject(element, listEPS);
                    const productEps = ProductService.getEps(pageEpsId);
                    const printAreas = productEps.imagePlaceHoldersEps.filter((image) => image.isPrintArea);
                    const productGuidelines = productEps.imagePlaceHoldersEps.filter(
                        (image) => image.isProductGuidelines
                    );
                    const loadImage = function (imageProperties) {
                        // eslint-disable-next-line no-async-promise-executor
                        return new Promise(async (resolve, reject) => {
                            // Load the image position and size
                            const objectCenterX = imageProperties.centerX;
                            const objectCenterY = imageProperties.centerY;
                            let imageWidth = imageProperties.width;
                            let imageHeight = imageProperties.height;

                            if (
                                printAreas?.length &&
                                isChildTemplateProduct &&
                                imageProperties.isPrintArea &&
                                !previewMode
                            ) {
                                console.log(
                                    'Child template product, loading print area',
                                    printAreas.map((pa) => [pa.id, pa.uuid]),
                                    [imageProperties.id, imageProperties.uuid]
                                );
                                const printArea = printAreas.find(
                                    (printArea) => printArea.uuid === imageProperties.uuid
                                );
                                if (printArea) {
                                    imageWidth = printArea.width;
                                    imageHeight = printArea.height;
                                    imageProperties = printArea;
                                }
                            }

                            if (
                                productGuidelines.length &&
                                isChildTemplateProduct &&
                                imageProperties.isProductGuidelines &&
                                !previewMode
                            ) {
                                const productGuideline = productGuidelines.find(
                                    (guideLine) => guideLine.uuid === imageProperties.uuid
                                );
                                if (productGuideline) {
                                    imageWidth = productGuideline.width;
                                    imageHeight = productGuideline.height;
                                    imageProperties = productGuideline;
                                }
                            }

                            //if the image is dynamic we'll convert the json obtained from the database to a js map
                            //unless we are on step3. On step3 we'll keep the json as it is to save it to the database
                            let imagesMap = new Map();
                            if (
                                imageProperties.dynamicImagesPath &&
                                !(imageProperties.dynamicImagesPath instanceof Map)
                            ) {
                                JSON.parse(imageProperties.dynamicImagesPath).map(function (element) {
                                    if (Array.isArray(element)) {
                                        imagesMap.set(element[0], element[1]);
                                    } else {
                                        imagesMap.set(Number(element.position), element.path);
                                    }
                                });
                            } else if (
                                imageProperties.dynamicImagesPath &&
                                imageProperties.dynamicImagesPath instanceof Map
                            ) {
                                imagesMap = imageProperties.dynamicImagesPath;
                            } else {
                                imagesMap = null;
                            }

                            // Parse image color. This deals with old and new color formats.
                            let color = MultipleFontService.parseColorFromProperty(imageProperties.color);

                            let allowNonUniformScaling = false;
                            if (username === 'stopandstair') {
                                allowNonUniformScaling = true;
                            }

                            if (imageProperties.calendar) {
                                const config =
                                    typeof imageProperties.calendarConfig == 'string'
                                        ? JSON.parse(imageProperties.calendarConfig)
                                        : imageProperties.calendarConfig;
                                imageProperties.calendarConfig = config;
                            }

                            const clipPathObject =
                                imageProperties.clipPath && typeof imageProperties.clipPath === 'string'
                                    ? tryParseJson(imageProperties.clipPath)
                                    : imageProperties.clipPath;

                            const img = new fabric.ImagePlaceholder(null, {
                                width: imageWidth,
                                height: imageHeight,
                                left: objectCenterX,
                                top: objectCenterY,
                                originX: 'center',
                                originY: 'center',
                                angle: imageProperties.rotation,
                                id: imageProperties.id,
                                uuid: imageProperties.uuid || uuidv4(),
                                strokeWidth: previewMode ? 0 : canvas.getVisibleStrokeWidth(),
                                strokeDashArray: canvas.getDashedArray(),
                                stroke: 'black',
                                type: 'image-placeholder',
                                centeredRotation: true,
                                skewX: imageProperties.skewX,
                                skewY: imageProperties.skewY,
                                disableSkewing: false,
                                lockUniScalingWithSkew: !allowNonUniformScaling, // Proportional scaling with skew
                                lockScalingFlip: false,
                                coverMaskArea: imageProperties.coverMaskArea,

                                fullColor: imageProperties.fullColor,
                                grading: imageProperties.grading,
                                zIndex: imageProperties.zIndex,
                                hasStaticImage: imageProperties.hasStaticImage,
                                imageUrl: imageProperties.imageUrl,
                                maskPath: imageProperties.maskPath,
                                dynamicImagesPath: imagesMap,
                                color: color,
                                imageLibraryId: imageProperties.imageLibraryId,

                                colorLibraryId: imageProperties.colorLibraryId,
                                colors: imageProperties.colors || [],
                                selectedColorPosition: 1,
                                rotationStep: imageProperties.rotationStep,
                                removeWhiteBackground: imageProperties.removeWhiteBackground,
                                RemoveBackground: imageProperties.RemoveBackground,
                                addedOnEdit:
                                    imageProperties.addedOnEdit ||
                                    (!previewMode &&
                                        isChildTemplateProduct &&
                                        (imageProperties.isPrintArea || imageProperties.isProductGuidelines)),
                                isMoonPhase: imageProperties.isMoonPhase,
                                CutoutFace: imageProperties.CutoutFace,
                                removeBackgroundCOP: imageProperties.removeBackgroundCOP,
                                CustomilyRemoveBackground: imageProperties.CustomilyRemoveBackground,
                                CustomilyFaceCutOut: imageProperties.CustomilyFaceCutOut,
                                CustomilyImageFilter: imageProperties.CustomilyImageFilter,

                                locked: imageProperties.locked,
                                selectable: !imageProperties.locked,
                                visible: imageProperties?.isGuide ? false : imageProperties.visible,
                                name: imageProperties.name,
                                preventEditMode: previewMode || (imagesMap && !imageProperties.allowEdit),
                                isOverlay: imageProperties.isOverlay,
                                isPrintArea: imageProperties.isPrintArea,
                                isColorOptions: imageProperties.isColorOptions,
                                podProductImagesMetadata: imageProperties.podProductImagesMetadata,
                                groupId: resolveLoadedElementGroupId(imageProperties),
                                mapConfig: imageProperties.mapConfig,
                                wordCloudConfig: imageProperties.wordCloudConfig,
                                wordSearchConfig: imageProperties.wordSearchConfig,
                                pageEpsId: pageEpsId || imageProperties.pageEpsId,
                                isLimitImageToPlaceholder: imageProperties.isLimitImageToPlaceholder,
                                textToImage: imageProperties.textToImage,
                                imageToImage: imageProperties.imageToImage,
                                imageToImageConfig: imageProperties.imageToImageConfig,
                                imageEffects: imageProperties.imageEffects,
                                isGuide: imageProperties.isGuide,
                                allowEdit: imageProperties.allowEdit,
                                crossword: imageProperties.crossword,
                                CrosswordConfig: imageProperties.CrosswordConfig,
                                crosswordSettings: imageProperties.crosswordSettings,
                                calendar: imageProperties.calendar,
                                calendarConfig: imageProperties.calendarConfig,
                                enablePerspective: imageProperties.enablePerspective,
                                perspectiveCoords:
                                    imageProperties.perspectiveCoords instanceof Array
                                        ? imageProperties.perspectiveCoords
                                        : JSON.parse(imageProperties.perspectiveCoords || '[]'),
                                testingUrl: imageProperties.testingUrl || null,
                                isProductGuidelines: imageProperties.isProductGuidelines,
                                clipPath: clipPathObject ? await loadClipPathFromObject(clipPathObject) : null,
                                initCenterX: imageProperties.initCenterX,
                                initCenterY: imageProperties.initCenterY,
                                initWidth: imageProperties.initWidth,
                                initHeight: imageProperties.initHeight,
                                initRotation: imageProperties.initRotation,
                                allowSwapLibraryPosition: imageProperties.allowSwapLibraryPosition,
                                limitMovement: imageProperties.limitMovement,
                                lockMovement: imageProperties.lockMovement,
                                lockScaling: imageProperties.lockScaling,
                                hasTextMask: imageProperties.hasTextMask,
                                patternConfig: parsePatternConfig(imageProperties.patternConfig)
                            });
                            // Image pattern replicas copy their origin's filtered bitmap from their first pass on;
                            //  before the setters below, so their passes over the still empty image are skipped too
                            trackImagePatternOnLoad(canvas, img, ProductService.getElementsLinks());
                            img.setOpacity(imageProperties.opacity);

                            //Image filters must be applied after image initialization
                            // Set image mode
                            if (!img.fullColor && !img.grading) {
                                img.setMode(fabric.IMAGE_MODE_SINGLE_COLOR);
                            } else if (!img.fullColor && img.grading) {
                                img.setMode(fabric.IMAGE_MODE_GRADING);
                            } else {
                                img.setMode(fabric.IMAGE_MODE_FULL_COLOR);
                            }
                            // Set image color
                            img.set('imageColor', ElementsColorService.colorToWeb(color));

                            await ElementsColorService.loadImageOutline(img, imageProperties, canvas);
                            await ElementsColorService.loadImageShadow(img, imageProperties, canvas);

                            if (previewMode) {
                                img.lockMovementY = true;
                                img.lockMovementX = true;
                                img.lockScalingX = true;
                                img.lockScalingY = true;
                                img.lockRotation = true;
                            }
                            img.setFlip(
                                !loadFromStep1 ? imageProperties.flipX : false,
                                !loadFromStep1 ? imageProperties.flipY : false
                            );

                            // If it's static -> load image content
                            if (imageProperties.hasStaticImage) {
                                TestImageService.showStaticImage(canvas, img, imageProperties);
                            } else if (imageProperties.mapConfig) {
                                const config =
                                    typeof imageProperties.mapConfig == 'string'
                                        ? JSON.parse(imageProperties.mapConfig)
                                        : imageProperties.mapConfig;
                                const image = await mapUtils.getStaticMapImage(config);
                                TestImageService.showStaticImage(canvas, img, { imageUrl: image });
                            } else if (imageProperties.dynamicImagesPath) {
                                // When loading step2 we should show the image on position 1 on the dynamic placeholder
                                await DynamicImageService.showDynamicImage(canvas, img, 1);
                            } else if (imageProperties.crossword) {
                                TestImageService.showStaticImage(canvas, img, {
                                    imageUrl: imageProperties.testingUrl?.length
                                        ? imageProperties.testingUrl
                                        : require('@/assets/images/crossword-example.png')
                                });
                            } else if (imageProperties.wordCloudConfig) {
                                TestImageService.showStaticImage(canvas, img, {
                                    imageUrl: imageProperties.testingUrl?.length
                                        ? imageProperties.testingUrl
                                        : require('@/assets/images/wordcloud-example.png')
                                });
                            } else if (imageProperties.wordSearchConfig) {
                                TestImageService.showStaticImage(canvas, img, {
                                    imageUrl: imageProperties.testingUrl?.length
                                        ? imageProperties.testingUrl
                                        : require('@/assets/images/wordsearch-example.png')
                                });
                            } else if (imageProperties.calendar) {
                                const output = await generateCustomilyCalendar(img);
                                TestImageService.showStaticImage(canvas, img, {
                                    imageUrl: output
                                });
                            } else {
                                TestImageService.showStaticImage(canvas, img, {
                                    imageUrl: imageProperties.testingUrl?.length
                                        ? imageProperties.testingUrl
                                        : require('@/assets/images/photo-example.png')
                                });
                            }

                            // Set image color
                            await DynamicImageService.changeDynamicImageColorPosition(img, 1, null, canvas);

                            if (loadFromStep1 && forProductTemplate && img.isColorOptions) {
                                img.locked = false;
                                img.visible = true;
                            }

                            // Apply mask if any
                            if (img.maskPath && getExtension(img.maskPath).toLowerCase() === 'svg') {
                                try {
                                    const svgResponse = await fetch(buildCloudFrontUrl(img.maskPath));
                                    if (!svgResponse.ok) {
                                        throw new Error(
                                            `Failed to fetch SVG mask (status ${svgResponse.status}): ${img.maskPath}`
                                        );
                                    }
                                    const svgText = await svgResponse.text();
                                    const sanitizedSvgString = sanitizeSvgString(svgText);
                                    img.originalMaskSvg = sanitizedSvgString;
                                    const svgMaskShape = await parseSvgMaskShape(sanitizedSvgString);
                                    await applySvgMaskClipPath(img, svgMaskShape);
                                    resolve(img);
                                } catch (error) {
                                    reject(error);
                                }
                            } else if (img.maskPath) {
                                const onMaskLoaded = async (maskImage) => {
                                    // If the mask is just a full black image don't set it, we don't need it now
                                    //  with the new image placeholder we don't use a fake mask anymore
                                    const fullMask = isImagePatternMember(img)
                                        ? isFullMaskBySrc(maskImage, ImageUtilService.isFullMask)
                                        : await ImageUtilService.isFullMask(maskImage);
                                    if (fullMask) {
                                        img.maskPath = null;
                                    } else {
                                        img.setMask(maskImage);
                                    }
                                    resolve(img);
                                };
                                const maskUrl = buildCloudFrontUrl(img.maskPath);
                                if (isImagePatternMember(img)) {
                                    // One <img> for the whole pattern's mask, as for its image
                                    sharedPatternImage(maskUrl).loaded.then(onMaskLoaded, () => reject());
                                } else {
                                    const maskImage = new Image();
                                    maskImage.onload = () => onMaskLoaded(maskImage);
                                    maskImage.onerror = function () {
                                        reject();
                                    };
                                    maskImage.crossOrigin = 'Anonymous';
                                    maskImage.src = maskUrl;
                                }
                            } else {
                                resolve(img);
                            }
                        });
                    };

                    tasks.push(async () => await loadImage(element));
                }
            }

            console.log('Adding vectors');
            if (vectorsEps) {
                for (const element of vectorsEps) {
                    const loadVector = async function (vector) {
                        const pageEpsId = getPageEpsIdObject(vector, listEPS);
                        const productEps = ProductService.getEps(pageEpsId);
                        const vectorEps = productEps.vectorEPSEps.find((e) => e.uuid === element.uuid);
                        const properties = { ...vector };

                        properties.groupId = resolveLoadedElementGroupId(properties);
                        properties.contentIsPdf = comesFromStep1 ? vectorEps?.contentIsPdf : properties?.contentIsPdf;
                        if (properties.contentIsPdf) {
                            properties.pdf = !comesFromStep1 ? properties.pdf : vectorEps.pdf || properties.pdf;
                        } else {
                            properties.svgString = !comesFromStep1
                                ? properties.svgString
                                : vectorEps?.svgString || properties.svgString;
                        }
                        if (comesFromStep1) {
                            properties.initWidth = vectorEps?.initWidth ?? properties.initWidth;
                            properties.initHeight = vectorEps?.initHeight ?? properties.initHeight;
                            properties.initCenterX = vectorEps?.initCenterX ?? properties.initCenterX;
                            properties.initCenterY = vectorEps?.initCenterY ?? properties.initCenterY;
                            properties.initRotation = vectorEps?.initRotation ?? properties.initRotation;
                            properties.limitMovement = vectorEps?.limitMovement ?? properties.limitMovement;
                            properties.lockScaling = vectorEps?.lockScaling ?? properties.lockScaling;
                        }
                        const dynamicVector = await DynamicVectorService.setVectorPlaceholderProperties(
                            properties,
                            canvas,
                            previewMode,
                            true,
                            pageEpsId || properties.pageEpsId
                        );

                        dynamicVector.addedOnEdit = properties.addedOnEdit;

                        if (loadFromStep1 && properties.isSolidBackground) {
                            dynamicVector.locked = false;
                        }

                        const clipPathObject =
                            properties.clipPath && typeof properties.clipPath === 'string'
                                ? tryParseJson(properties.clipPath)
                                : properties.clipPath;
                        dynamicVector.clipPath = clipPathObject ? await loadClipPathFromObject(clipPathObject) : null;

                        if (properties.starMapConfig && isObjectOrStringifiedObject(properties.starMapConfig)) {
                            const config =
                                typeof properties.starMapConfig == 'string'
                                    ? JSON.parse(properties.starMapConfig)
                                    : properties.starMapConfig;

                            const mutex = ProductService.getStarMapMutex();
                            const starmapSVG = await starMapUtils.updatedDisplayStarMap(
                                starMapUtils.updatedConfig(config),
                                mutex
                            );
                            await DynamicVectorService.loadSVGStringOnPlaceholder(starmapSVG, dynamicVector, canvas);
                        } else if (properties.allowUserUpload) {
                            if (properties.contentIsPdf) {
                                dynamicVector.set('pdf', properties.pdf);
                                await dynamicVector._contentLoadPromise;
                            } else if (properties.svgString) {
                                await DynamicVectorService.loadSVGStringOnPlaceholder(
                                    properties.svgString,
                                    dynamicVector,
                                    canvas
                                );
                            } else {
                                TestImageService.showStaticImageSvg(canvas, dynamicVector, {
                                    imageUrl: require('@/assets/images/vector-example.svg')
                                });
                            }
                        } else if (
                            properties.vectors &&
                            properties.vectors.length == 0 &&
                            !properties.vectorLibraryId
                        ) {
                            TestImageService.showStaticImageSvg(canvas, dynamicVector, {
                                imageUrl: require('@/assets/images/dynamic-vector.svg')
                            });
                        }
                        return dynamicVector;
                    };

                    tasks.push(async () => await loadVector(element));
                }
            }

            // Create all the canvas elements
            const elements = await ExecutionService.runTasksInChunks(tasks);
            LibrariesAPIService.clearCachedLibraries();
            const OUTPAGEEPS_ID = 'outpageeps';
            const epsElements = elements.reduce((epsElements, element) => {
                if (previewElementIds && previewElementIds.has(element.uuid)) return epsElements;
                const pageEpsId = String(element.pageEpsId || OUTPAGEEPS_ID);
                (epsElements[pageEpsId] ? epsElements[pageEpsId] : (epsElements[pageEpsId] = [])).push(element);
                return epsElements;
            }, []);

            // Resize all the elements from the step 1 to this step
            if (loadFromStep1) {
                for (const [epsId, epsElement] of Object.entries(epsElements)) {
                    CanvasUtilService.scaleCanvasElementsForPreview(
                        canvas,
                        epsElement,
                        epsId == OUTPAGEEPS_ID ? null : epsId
                    );
                }
            } else if (isChildTemplateProduct && !previewMode) {
                for (const [epsId, epsElement] of Object.entries(epsElements)) {
                    const newElements = epsElement.filter((el) => el.addedOnEdit);
                    CanvasUtilService.scaleCanvasElementsForPreview(
                        canvas,
                        newElements,
                        epsId == OUTPAGEEPS_ID ? null : epsId,
                        true
                    );
                }
            }
            // Scale added canvas elements
            else if (elements.some((e) => e.addedOnEdit)) {
                for (const [epsId, epsElement] of Object.entries(epsElements)) {
                    const newElements = epsElement.filter((el) => el.addedOnEdit);
                    CanvasUtilService.scaleCanvasElementsForPreview(
                        canvas,
                        newElements,
                        epsId == OUTPAGEEPS_ID ? null : epsId
                    );
                    for (const element of newElements) {
                        if (element.type === 'text-transform') {
                            element?.updateFromPreviewScaling();
                        }
                        element.addedOnEdit = false;
                    }
                }
            } else if (elements.some((e) => e.needUpdatePreview)) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                for (const [epsId, epsElement] of Object.entries(epsElements)) {
                    const newElements = epsElement?.filter((el) => el.needUpdatePreview) || [];
                    for (const element of newElements) {
                        element?.updateFromPreviewScaling();
                        element.needUpdatePreview = false;
                    }
                }
            }

            // Sort elements by zIndex in ascending order
            const orderedElements = elements.sort((a, b) => {
                return a.zIndex - b.zIndex;
            });

            // Add all the elements to the canvas now
            for (const canvasElement of orderedElements) {
                if (canvasElement) {
                    canvas.add(canvasElement, true, false);

                    // TODO: check if this is still needed
                    if (
                        canvasElement.type === 'bounded-text' &&
                        !(canvasElement.useAsMask || canvasElement.useAsCutout)
                    ) {
                        // Apply texture to text elements
                        if (canvasElement.textures && canvasElement.textures.length > 0) {
                            await TextureService.changeFontTexture(
                                canvasElement.textures[0].texturePath,
                                canvasElement,
                                1
                            );
                            if (canvasElement.shadowType != null) {
                                canvasElement.set?.('shadowType', null);
                                canvasElement.shadowType = null;
                            }
                            canvas.renderAll();
                        }
                    }
                }
            }

            // For product templates, create the color option element, scale it to the canvas size, position it below the print area element and lock it
            if (loadFromStep1 && forProductTemplate && !forMyProductBases) {
                addProductColorElement(canvas, colorOptionsMetadata, 'Product colors', false);
            }

            const emptyEps = canvas.getElementsPageEps()?.filter((w) => canvas.getPageEpsElements(w).length == 0);
            for (const eps of emptyEps) {
                canvas.removePageEps(eps, false);
            }

            // update Page Eps visibility and locked from elements
            updatePageEpsVisibilityAndLocked(canvas);

            canvas.renderAll();

            const scope = { editMode: editMode };

            for (const object of canvas._objects.filter(
                (o) => o.type === 'bounded-text' && (o.useAsMask || o.useAsCutout)
            )) {
                if (object.useAsMask) TextService.applyTextMask(canvas, scope, object.uuid);
                else TextService.applyTextCutout(canvas, scope, object.uuid);
            }

            for (const group of groupsToLoad) {
                canvas.addGroup(group, true);
            }

            const elementsLinks = ProductService.getElementsLinks() || [];
            // A replica unlocked in a preview stays detached from its origin across loads
            sourceElementUtils.populateElementsLinks(elementsLinks, canvas, TextService, { keepSavedLock: true });

            // If we ungroup a child group on step 1, then on step2 the group appears empty.
            // This cleanup only applies when loading groups from step1 during non-edit flows.
            if (shouldCleanupEmptyGroupsOnStep2Load(loadFromStep1, editMode)) {
                removeEmptyGroups(
                    canvas,
                    groupsToLoad.map((g) => g.id),
                    orderedElements
                );
            }

            if (needColorsUpgrade) {
                console.log('Upgrading colors!');
                let objects = canvas._objects;

                for (const element of objects) {
                    if (element.type === 'bounded-text' || element.type === 'text-transform') {
                        element.fontColorsMap.forEach(function (value, key, map) {
                            map.set(key, MultipleFontService.upgradeOldColor(value));
                        });

                        element.outlineColorsMap.forEach(function (value, key, map) {
                            map.set(key, MultipleFontService.upgradeOldColor(value));
                        });

                        element.color = MultipleFontService.upgradeOldColor(element.color);
                        element.outlineColor = MultipleFontService.upgradeOldColor(element.outlineColor);
                    } else if (element.type === 'path-itext') {
                        element.fontColorsMap.forEach(function (value, key, map) {
                            map.set(key, MultipleFontService.upgradeOldColor(value));
                        });

                        element.outlineColorsMap.forEach(function (value, key, map) {
                            map.set(key, MultipleFontService.upgradeOldColor(value));
                        });

                        element.color = MultipleFontService.upgradeOldColor(element.color);
                    } else if (element.type === 'image-placeholder') {
                        element.color = MultipleFontService.upgradeOldColor(element.color);
                    }
                }
            }

            setTimeout(() => {
                canvas.fire('canvas:loaded');
            }, 0);
        }

        function getLastObjectId(canvas) {
            let objects = canvas.getObjects();
            let id = 0;
            for (let n = 0; n < objects.length; ++n) {
                id = Math.max(id, objects[n].id);
            }
            return id;
        }

        function addProductColorElement(
            canvas,
            colorOptionsMetadata,
            elementName = 'Product colors',
            saveHistory = true
        ) {
            const colorsDynamicImage = DynamicImageService.createDynamicImage(canvas, getLastObjectId(canvas) + 1, {
                zIndex: 0,
                pageEpsId: null
            });
            canvas.add(colorsDynamicImage, true, saveHistory);

            colorsDynamicImage.name = elementName;
            // Set the element as a color option element
            toggleElementColorOption(colorsDynamicImage, canvas);

            // Lock it, make it visible and scale it to the entire canvas size
            colorsDynamicImage.set('locked', colorOptionsMetadata ? true : false);
            colorsDynamicImage.set('visible', true);
            colorsDynamicImage.setWidth(canvas.getArtworkWidth());
            colorsDynamicImage.setHeight(canvas.getArtworkHeight());
            colorsDynamicImage.setCoords();

            // Make sure the color element is below the print area element and guidelines element
            const zIndexesPrintAreaAndGuidelines = canvas
                .getObjects()
                .filter((o) => o.isPrintArea || o.isProductGuidelines)
                .map((item) => item.zIndex);
            const zIndexMin = Math.min(...zIndexesPrintAreaAndGuidelines, canvas.getMinZindexPageEps());
            colorsDynamicImage.zIndex = zIndexMin - 1;

            if (colorOptionsMetadata) {
                // Set all the images for each color
                colorsDynamicImage.podProductImagesMetadata = colorOptionsMetadata;
                for (const image of colorOptionsMetadata.images) {
                    colorsDynamicImage.dynamicImagesPath.set(image.position, image.path);
                }
            }
            DynamicImageService.showDynamicImage(canvas, colorsDynamicImage, 1);

            return colorsDynamicImage;
        }

        function processCanvasObjectsForSaving(canvas, backgroundType, previewPath) {
            for (const canvasObject of canvas.getObjects()) {
                // Texts
                if (canvasObject.type == 'bounded-text' || canvasObject.type == 'text-transform') {
                    const {
                        fontsMap,
                        fontColorsMap,
                        outlineColorsMap,
                        strokeColor,
                        strokeColorsMap,
                        shadowColor,
                        shadowColorsMap
                    } = MultipleFontService.processTextBeforeSaving(canvasObject);
                    ProductService.addPreviewText({
                        centerX: canvasObject.left,
                        centerY: canvasObject.top,
                        color: canvasObject.color,
                        rotation: canvasObject.angle,
                        skewX: canvasObject.skewX,
                        skewY: canvasObject.skewY,
                        width: canvasObject.width,
                        height: canvasObject.height,
                        minSizePx: canvasObject.minSizePx,
                        maxSizePx: canvasObject.maxSizePx,
                        multiline: canvasObject.multiline,
                        caps: canvasObject.caps,
                        fontPath: canvasObject.fontPath,
                        textAlign: canvasObject.getTextAlign(),
                        outlineColor: canvasObject.outlineColor,
                        outlineWidth: canvasObject.getOutlineWidth(),
                        outlineColorsMap: outlineColorsMap,
                        outlineColorLibraryId: canvasObject.outlineColorLibraryId,
                        zIndex: canvasObject.zIndex,
                        id: canvasObject.id,
                        uuid: canvasObject.uuid,
                        fontsMap: fontsMap,
                        fontColorsMap: fontColorsMap,
                        Itext: canvasObject.Itext,
                        textures: canvasObject.textures,
                        tracking: canvasObject.tracking,
                        lineSpacing: canvasObject.lineSpacing,
                        colorLibraryId: canvasObject.colorLibraryId,
                        fontLibraryId: canvasObject.fontLibraryId,
                        version: canvasObject.version,
                        addedOnEdit: false,
                        locked: canvasObject.locked,
                        visible: canvasObject.visible,
                        name: canvasObject.name,
                        prefix: canvasObject.prefix,
                        suffix: canvasObject.suffix,
                        betweenCharacters: canvasObject.betweenCharacters,
                        betweenWords: canvasObject.betweenWords,
                        ligatures: canvasObject.ligatures,
                        referenceId: canvasObject.referenceId,
                        isPrintArea: canvasObject.isPrintArea,
                        isOverlay: canvasObject.isOverlay,
                        groupId: canvasObject.groupId,
                        textVerticalAlign: canvasObject.textVerticalAlign,
                        allowEdit: canvasObject.allowEdit,
                        oldAllowEdit: canvasObject.oldAllowEdit,
                        /*If the text uses the previous version of allowEdit (without a bounding box), these properties are left as null.
                        Once they have a value, the new version is considered active.*/
                        initCenterX:
                            !canvasObject.oldAllowEdit && canvasObject.getTextLeft ? canvasObject.getTextLeft() : null,
                        initCenterY:
                            !canvasObject.oldAllowEdit && canvasObject.getTextTop ? canvasObject.getTextTop() : null,
                        initRotation:
                            !canvasObject.oldAllowEdit && canvasObject.getTextAngle
                                ? canvasObject.getTextAngle()
                                : null,
                        initFontSize:
                            !canvasObject.oldAllowEdit && canvasObject.getFontSize ? canvasObject.getFontSize() : null,
                        /* --- */
                        pageEpsId: canvasObject.pageEpsId,
                        flipX: canvasObject.getFlip().flipX,
                        flipY: canvasObject.getFlip().flipY,
                        testingText: canvasObject.text,
                        isTextPath: canvasObject.isTextPath || false,
                        textPath: canvasObject.textPath || null,
                        textPathAutoFit: canvasObject.textPathAutoFit !== false,
                        transformOptions: canvasObject.transformOptions || {},
                        clipPath: canvasObject.clipPath,
                        patternConfig: canvasObject.patternConfig ? JSON.stringify(canvasObject.patternConfig) : null,
                        shapeConfig: serializeTextShapeConfig(canvasObject.shapeConfig),
                        useAsMask: canvasObject.useAsMask || false,
                        useAsCutout: canvasObject.useAsCutout || false,
                        verticalText: canvasObject.verticalText || false,
                        autofillSource: canvasObject.autofillSource || '',
                        strokeColor: strokeColor || '',
                        strokeWidth: (canvasObject.getStrokeWidth && canvasObject.getStrokeWidth()) || 0,
                        dateFormat: canvasObject.dateFormat || {},
                        strokeColorsMap,
                        strokeColorLibraryId: canvasObject.strokeColorLibraryId,
                        /* shadow */
                        shadowType: canvasObject.shadowType,
                        shadowOffset: canvasObject.shadowOffset,
                        shadowAngle: canvasObject.shadowAngle,
                        shadowBlur: canvasObject.shadowBlur,
                        shadowColor: shadowColor,
                        shadowColorsMap: shadowColorsMap,
                        shadowColorLibraryId: canvasObject.shadowColorLibraryId || null,
                        metadata: canvasObject.metadata || '',
                        sourceElement: canvasObject.sourceElement,
                        isPersonalizableText: canvasObject.isPersonalizableText,
                        defaultValue: canvasObject.defaultValue
                    });
                }
                // Circular Texts
                else if (canvasObject.type == 'path-itext') {
                    const { fontsMap, fontColorsMap, outlineColorsMap } = MultipleFontService.processTextBeforeSaving(
                        canvasObject
                    );
                    ProductService.addPreviewCircularText({
                        centerX: canvasObject.left,
                        centerY: canvasObject.top,
                        color: canvasObject.color,
                        rotation: canvasObject.angle,
                        skewX: canvasObject.skewX,
                        skewY: canvasObject.skewY,
                        horizontalDiameter: canvasObject.horizontalDiameter,
                        verticalDiameter: canvasObject.verticalDiameter,
                        fontSize: canvasObject.fontSize,
                        minSizePx: canvasObject.minSizePx,
                        maxSizePx: canvasObject.maxSizePx,
                        caps: canvasObject.caps,
                        fontPath: canvasObject.fontPath,
                        textAlign: canvasObject.textAlign,
                        convex: canvasObject.convex,
                        tracking: canvasObject.tracking,
                        outlineColor: canvasObject.stroke,
                        outlineWidth: canvasObject.strokeWidth,
                        outlineColorsMap: outlineColorsMap,
                        outlineColorLibraryId: canvasObject.outlineColorLibraryId,
                        zIndex: canvasObject.zIndex,
                        id: canvasObject.id,
                        uuid: canvasObject.uuid,
                        fontsMap: fontsMap,
                        fontColorsMap: fontColorsMap,
                        colorLibraryId: canvasObject.colorLibraryId,
                        fontLibraryId: canvasObject.fontLibraryId,
                        startAngle: canvasObject.startAngle,
                        endAngle: canvasObject.endAngle,
                        addedOnEdit: false,
                        locked: canvasObject.locked,
                        visible: canvasObject.visible,
                        name: canvasObject.name,
                        defaultValue: canvasObject.defaultValue,
                        isPersonalizableText: canvasObject.isPersonalizableText !== false,
                        prefix: canvasObject.prefix,
                        suffix: canvasObject.suffix,
                        betweenCharacters: canvasObject.betweenCharacters,
                        betweenWords: canvasObject.betweenWords,
                        referenceId: canvasObject.referenceId,
                        isPrintArea: canvasObject.isPrintArea,
                        isOverlay: canvasObject.isOverlay,
                        groupId: canvasObject.groupId,
                        pageEpsId: canvasObject.pageEpsId,
                        flipX: canvasObject.getFlip().flipX,
                        flipY: canvasObject.getFlip().flipY,
                        testingText: canvasObject.text,
                        clipPath: canvasObject.clipPath
                    });
                }
                // Images placeholders
                else if (canvasObject.type == 'image-placeholder') {
                    ProductService.addPreviewPlaceholderImage({
                        centerX: canvasObject.left,
                        centerY: canvasObject.top,
                        rotation: canvasObject.angle,
                        skewX: canvasObject.skewX,
                        skewY: canvasObject.skewY,
                        width: canvasObject.getWidth(),
                        height: canvasObject.getHeight(),
                        color: canvasObject.color,
                        fullColor: canvasObject.fullColor,
                        grading: canvasObject.grading,
                        opacity: canvasObject.getOpacity(),
                        zIndex: canvasObject.zIndex,
                        id: canvasObject.id,
                        uuid: canvasObject.uuid,
                        hasStaticImage: canvasObject.hasStaticImage,
                        imageUrl: canvasObject.imageUrl,
                        maskPath: canvasObject.maskPath,
                        dynamicImagesPath: canvasObject.dynamicImagesPath,
                        imageLibraryId: canvasObject.imageLibraryId,
                        colors: canvasObject.colors,
                        colorLibraryId: canvasObject.colorLibraryId,
                        rotationStep: canvasObject.rotationStep,
                        coverMaskArea: canvasObject.coverMaskArea,
                        removeWhiteBackground: canvasObject.removeWhiteBackground,
                        RemoveBackground: canvasObject.RemoveBackground,
                        addedOnEdit: false,
                        locked: canvasObject.locked,
                        visible: canvasObject.visible,
                        name: canvasObject.name,
                        isPrintArea: canvasObject.isPrintArea,
                        isOverlay: canvasObject.isOverlay,
                        isColorOptions: canvasObject.isColorOptions,
                        podProductImagesMetadata: canvasObject.podProductImagesMetadata,
                        groupId: canvasObject.groupId,
                        mapConfig: canvasObject.mapConfig,
                        wordCloudConfig: canvasObject.wordCloudConfig,
                        wordSearchConfig: canvasObject.wordSearchConfig,
                        CutoutFace: canvasObject.CutoutFace,
                        removeBackgroundCOP: canvasObject.removeBackgroundCOP,
                        CustomilyRemoveBackground: canvasObject.CustomilyRemoveBackground,
                        CustomilyFaceCutOut: canvasObject.CustomilyFaceCutOut,
                        CustomilyImageFilter: canvasObject.CustomilyImageFilter,
                        pageEpsId: canvasObject.pageEpsId,
                        flipX: canvasObject.getFlip()?.flipX || false,
                        flipY: canvasObject.getFlip()?.flipY || false,
                        isLimitImageToPlaceholder: canvasObject.isLimitImageToPlaceholder,
                        textToImage: canvasObject.textToImage,
                        imageToImage: canvasObject.imageToImage,
                        imageToImageConfig: canvasObject.imageToImageConfig,
                        imageEffects: canvasObject.imageEffects,
                        isGuide: canvasObject.isGuide,
                        allowEdit: canvasObject.allowEdit,
                        crossword: canvasObject.crossword,
                        CrosswordConfig: canvasObject.CrosswordConfig,
                        crosswordSettings: canvasObject.crosswordSettings,
                        calendar: canvasObject.calendar,
                        calendarConfig: canvasObject.calendarConfig,
                        enablePerspective: canvasObject.enablePerspective,
                        perspectiveCoords: canvasObject.perspectiveCoords,
                        outlineWidth: canvasObject.outlineWidth,
                        outlineColorsMap: canvasObject.outlineColorsMap,
                        outlineColorLibraryId: canvasObject.outlineColorLibraryId,
                        shadowType: canvasObject.shadowType,
                        shadowOffset: canvasObject.shadowOffset,
                        shadowAngle: canvasObject.shadowAngle,
                        shadowBlur: canvasObject.shadowBlur,
                        shadowColor: canvasObject.shadowColor,
                        shadowColorsMap: canvasObject.shadowColorsMap,
                        shadowColorOption: canvasObject.shadowColorOption,
                        shadowColorLibraryId: canvasObject.shadowColorLibraryId,
                        testingUrl: canvasObject.testingUrl,
                        isProductGuidelines: canvasObject.isProductGuidelines,
                        clipPath: canvasObject.hasTextMask ? null : canvasObject.clipPath || null,
                        hasTextMask: canvasObject.hasTextMask,
                        isMoonPhase: canvasObject.isMoonPhase || false,
                        initCenterX: canvasObject.initCenterX,
                        initCenterY: canvasObject.initCenterY,
                        initWidth: canvasObject.initWidth,
                        initHeight: canvasObject.initHeight,
                        initRotation: canvasObject.initRotation,
                        allowSwapLibraryPosition: canvasObject.allowSwapLibraryPosition,
                        limitMovement: canvasObject.limitMovement,
                        lockMovement: canvasObject.lockMovement,
                        lockScaling: canvasObject.lockScaling,
                        patternConfig: canvasObject.patternConfig ? JSON.stringify(canvasObject.patternConfig) : null
                    });
                }
                // Vector EPS
                else if (canvasObject.type === 'vector-placeholder') {
                    ProductService.addPreviewVectorEPS({
                        centerX: canvasObject.left,
                        centerY: canvasObject.top,
                        rotation: canvasObject.angle,
                        skewX: canvasObject.skewX,
                        skewY: canvasObject.skewY,
                        width: canvasObject.getWidth(),
                        height: canvasObject.getHeight(),
                        opacity: canvasObject.opacity,
                        vectors: canvasObject.vectors,
                        colors: canvasObject.colors,
                        allowUserUpload: canvasObject.allowUserUpload,
                        zIndex: canvasObject.zIndex,
                        id: canvasObject.id,
                        uuid: canvasObject.uuid,
                        colorLibraryId: canvasObject.colorLibraryId,
                        vectorLibraryId: canvasObject.vectorLibraryId,
                        applyColoring: canvasObject.applyColoring,
                        coverMaskArea: canvasObject.coverMaskArea,
                        rotationStep: canvasObject.rotationStep,
                        addedOnEdit: false,
                        locked: canvasObject.locked,
                        visible: canvasObject.visible,
                        name: canvasObject.name,
                        isPrintArea: canvasObject.isPrintArea,
                        isOverlay: canvasObject.isOverlay,
                        groupId: canvasObject.groupId,
                        starMapConfig: canvasObject.starMapConfig,
                        qrConfig: canvasObject.qrConfig,
                        contentIsPdf: canvasObject.contentIsPdf,
                        vectorContent: canvasObject.contentIsPdf ? canvasObject.pdf : canvasObject.svgString,
                        allowEdit: canvasObject.allowEdit,
                        pageEpsId: canvasObject.pageEpsId,
                        flipX: canvasObject.getFlip().flipX,
                        flipY: canvasObject.getFlip().flipY,
                        isLimitImageToPlaceholder: undefined,
                        clipPath: canvasObject.clipPath,
                        isSolidBackground: canvasObject.isSolidBackground,
                        allowSwapLibraryPosition: canvasObject.allowSwapLibraryPosition,
                        initCenterX: canvasObject.initCenterX,
                        initCenterY: canvasObject.initCenterY,
                        initWidth: canvasObject.initWidth,
                        initHeight: canvasObject.initHeight,
                        initRotation: canvasObject.initRotation,
                        limitMovement: canvasObject.limitMovement,
                        lockScaling: canvasObject.lockScaling
                    });
                }
            }

            if (backgroundType === 'image') {
                ProductService.setPreviewImage(previewPath);
                ProductService.setPreviewBgColor(null);
            } else {
                ProductService.setPreviewImage(null);
                ProductService.setPreviewBgColor(canvas.backgroundColor);
            }

            ProductService.setPreviewImgSize(canvas.getArtworkWidth(), canvas.getArtworkHeight());
        }

        async function saveStep2(
            canvas,
            isManagedUser,
            editMode,
            replacedBackground,
            backgroundImageFile,
            backgroundType
        ) {
            let prod;
            if (isManagedUser && editMode) {
                const guid = ProductService.getProduct().id;
                prod = await ProductAPIService.getProduct(guid);
            }

            // We first deselect all the active elements on the canvas in order for their
            //  attributes to get updated if modified using multiple selection
            canvas.discardActiveObject().renderAll();

            const previewPath = ProductService.getPreviewImage();
            ProductService.clearPreview();
            ProductService.addGroups(canvas.getElementsGroups());
            ProductService.updateEPSFromPreview(canvas.getElementsPageEps());

            // We reset visible value for each locked element when the user is a child account
            if (prod) {
                const listPreviews = prod.listPreviews?.length ? prod.listPreviews : [prod.preview];
                for (const canvasObject of canvas.getObjects()) {
                    if (canvasObject.locked) {
                        // Text objects
                        if (canvasObject.type === 'bounded-text' || canvasObject.type === 'text-transform') {
                            for (const preview of listPreviews) {
                                const previewElement = preview.textsPreview.find(
                                    (elCanvas) => elCanvas.uuid === canvasObject.uuid
                                );
                                if (previewElement) {
                                    canvasObject.visible = previewElement.visible;
                                }
                            }
                        }
                        // Path text object
                        else if (canvasObject.type === 'path-itext') {
                            for (const preview of listPreviews) {
                                const previewElement = preview.circularTextsPreview.find(
                                    (elCanvas) => elCanvas.uuid === canvasObject.uuid
                                );
                                if (previewElement) {
                                    canvasObject.visible = previewElement.visible;
                                }
                            }
                        }
                        // Image placeholder object
                        else if (canvasObject.type === 'image-placeholder') {
                            for (const preview of listPreviews) {
                                const previewElement = preview.imagePlaceHoldersPreview.find(
                                    (elCanvas) => elCanvas.uuid === canvasObject.uuid
                                );
                                if (previewElement) {
                                    canvasObject.visible = previewElement.visible;
                                }
                            }
                        }
                        // Vector EPS
                        else if (canvasObject.type === 'vector-placeholder') {
                            for (const preview of listPreviews) {
                                const previewElement = preview.vectorEPSPreview.find(
                                    (elCanvas) => elCanvas.uuid === canvasObject.uuid
                                );
                                if (previewElement) {
                                    canvasObject.visible = previewElement.visible;
                                }
                            }
                        }
                    }
                }
            }

            let image = previewPath;

            // We'll only save the background image it was modified, otherwise we'll keep the one that we had already
            if (!(!replacedBackground && editMode) && backgroundImageFile) {
                const response = await uploadImageToS3('preview', backgroundImageFile);
                image = response.Path;
            }

            processCanvasObjectsForSaving(canvas, backgroundType, image);
        }
        return {
            saveStep2,
            loadStep2IntoCanvas,
            addProductColorElement,
            processCanvasObjectsForSaving
        };
    }
})();
